import React, { useContext, useEffect, useRef, useState } from "react";
import * as QuickSightEmbedding from "amazon-quicksight-embedding-sdk";
import { Alert, Box, Spinner } from "@amzn/awsui-components-react";
import { useQuery } from "@tanstack/react-query";
import { getAppSetting } from "../../config/AppSettings";

import { DashboardApiFactory } from "../../lusca-search-api/LuscaSearchApiFactory";
import DarkModeContext from "../../contexes/DarkModeContext";
import { compressAndSerializeData, decompressAndDeserializeData } from "../../helpers/compressionUtils";
import { getMidwayJwtToken } from "../../auth/MidwayJwtToken";
import { Stage } from "../../lusca-search-api/generated-src";



interface QuickSightDashboardProps {
  stage: Stage;
  dashboardType?: string;
  onLoad?: () => void;
  searchParams: URLSearchParams;
  setSearchParams: (params: URLSearchParams) => void;
  setShareHandler: React.Dispatch<React.SetStateAction<(() => Promise<string>) | null>>;
}

const CONTAINER_HEIGHT = "calc(100vh - 60px)";

export const QuickSightDashboard: React.FC<QuickSightDashboardProps> = ({
  stage,
  dashboardType,
  onLoad,
  searchParams,
  setSearchParams,
  setShareHandler,
}) => {
  const containerRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const dashboardExperienceRefs = useRef<{
    [key: string]: QuickSightEmbedding.DashboardExperience | null;
  }>({});
  const embedAttemptedRefs = useRef<{ [key: string]: boolean }>({});
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const darkModeContext = useContext(DarkModeContext);
  const [isDarkTheme, setIsDarkTheme] = useState(darkModeContext);

  // Create persistent containers once so we dont re-embed every time
  useEffect(() => {
    if (mainContainerRef.current) {
      const stages = dashboardType === "testing" ? ["testing"] : [Stage.Prod, Stage.Gamma];
      stages.forEach((stageKey) => {
        if (!containerRefs.current[stageKey]) {
          const div = document.createElement("div");
          div.style.width = "100%";
          div.style.height = CONTAINER_HEIGHT;
          div.style.position = "absolute";
          div.style.top = "0";
          div.style.left = "0";
          div.style.display = "none"; // Initially hidden
          containerRefs.current[stageKey] = div;
          mainContainerRef.current!.appendChild(div);
        }
      });
    }
  }, [dashboardType]);

  // Show/hide containers based on current stage
  useEffect(() => {
    const activeKey = dashboardType === "testing" ? "testing" : stage;
    Object.keys(containerRefs.current).forEach((stageKey) => {
      const container = containerRefs.current[stageKey];
      if (container) {
        container.style.display = stageKey === activeKey ? "block" : "none";
      }
    });
  }, [stage, dashboardType]);

  const dashboardApi = DashboardApiFactory();

  const THEMES = {
    DARK:
      dashboardType === "testing"
        ? "arn:aws:quicksight:us-east-1:545238427733:theme/27553d4d-fcec-4ab8-8f95-701f12007dc8"
        : stage === Stage.Gamma
        ? "arn:aws:quicksight:us-east-1:239039480348:theme/cloudscape"
        : "arn:aws:quicksight:us-east-1:800989574994:theme/e42b2ecd-7436-45a0-a032-52469c99af64",
    LIGHT: "arn:aws:quicksight::aws:theme/CLASSIC",
  };

  const applyTheme = async (newTheme: boolean) => {
    const activeKey = dashboardType === "testing" ? "testing" : stage;
    const currentDashboard = dashboardExperienceRefs.current[activeKey];
    if (currentDashboard) {
      try {
        const setThemePromise = new Promise((resolve, reject) => {
          try {
            const result = currentDashboard.setTheme(
              newTheme ? THEMES.DARK : THEMES.LIGHT
            );
            resolve(result);
          } catch (e) {
            reject(e);
          }
        });

        await Promise.race([
          setThemePromise,
          new Promise((_, reject) => {
            setTimeout(
              () => reject(new Error("Theme change timed out")),
              30000
            );
          }),
        ]);

        setIsDarkTheme(newTheme);
      } catch (err) {
        console.warn(
          "Theme change timed out (this is expected and harmless - dashboard continues to work):",
          err
        );
      }
    } else {
      console.log("Dashboard not ready, setting initial theme state");
      setIsDarkTheme(newTheme);
    }
  };

  useEffect(() => {
    if (darkModeContext !== isDarkTheme) {
      applyTheme(darkModeContext);
    }
  }, [applyTheme, darkModeContext, isDarkTheme]);

  const { data: embedUrlData } = useQuery(
    ["dashboardEmbed", stage, dashboardType || "main"],
    async () => {
      if (dashboardType === "testing") {
        const url = `https://${getAppSetting("apiUrl")}/jwt/dashboard/testing?stage=${stage}`;
        const response = await fetch(`https://${getAppSetting("apiUrl")}/jwt/dashboard/testing?stage=${stage}`, {
          headers: {
            'Authorization': `Bearer ${await getMidwayJwtToken()}`,
            'Content-Type': 'application/json'
          }
        });
        return await response.json();
      } else {
        const { data } = await dashboardApi.getDashboardEmbedUrl(stage);
        return data;
      }
    },
    {
      enabled: !!stage,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,
    }
  );

  const getFilters = async () => {
    const activeKey = dashboardType === "testing" ? "testing" : stage;
    const experience = dashboardExperienceRefs.current[activeKey];
    if (!experience) throw new Error("Dashboard not loaded");

    const sheetId = await experience.getSelectedSheetId();
    return experience.getFilterGroupsForSheet(sheetId);
  };

  const restoreFilters = async (filtersParams: string) => {
    const activeKey = dashboardType === "testing" ? "testing" : stage;
    const experience = dashboardExperienceRefs.current[activeKey];
    if (!experience) throw new Error("Dashboard not loaded");

    const parsedFilterGroups = decompressAndDeserializeData<any[]>(filtersParams);
    const existingFilterGroups = await getFilters();

    // updateFilterGroups will silently ignore the whole request if a request contains
    // any FilterGroupId that doesn't already exist.
    const changedFilterGroups = parsedFilterGroups.filter((newFg) => {
      const existing = existingFilterGroups.find(
        (e) => e.FilterGroupId === newFg.FilterGroupId
      );
      return existing && JSON.stringify(existing) !== JSON.stringify(newFg);
    });
    const addedFilterGroups = parsedFilterGroups.filter(
      (newFg) =>
        !existingFilterGroups.some(
          (e) => e.FilterGroupId === newFg.FilterGroupId
        )
    );

    if (changedFilterGroups.length !== 0) {
      await experience.updateFilterGroups(changedFilterGroups);
    }
    if (addedFilterGroups.length !== 0) {
      await experience.addFilterGroups(addedFilterGroups);
    }
  };

  useEffect(() => {
    // If dashboard already exists for this stage, just show it
    const activeKey = dashboardType === "testing" ? "testing" : stage;
    if (dashboardExperienceRefs.current[activeKey]) {
      setIsLoading(false);
      return;
    }

    // Only embed if dashboard has not previously been embedded
    if (
      embedUrlData?.embedUrl &&
      containerRefs.current[activeKey] &&
      !embedAttemptedRefs.current[activeKey]
    ) {
      embedAttemptedRefs.current[activeKey] = true;
      const embedDashboard = async () => {
        try {
          setIsLoading(true);
          const embeddingContext =
            await QuickSightEmbedding.createEmbeddingContext();
          const experience = await embeddingContext.embedDashboard(
            {
              url: embedUrlData.embedUrl,
              container: containerRefs.current[activeKey]!,
              width: "100%",
              height: "100%",
              resizeHeightOnSizeChangedEvent: true,
              onChange: (event: QuickSightEmbedding.EmbeddingEvents) => {
                if (event.eventName === "FRAME_LOADED") {
                  setIsLoading(false);
                  onLoad?.();
                  const filtersParam = searchParams.get("filters");
                  if (filtersParam) {
                    restoreFilters(filtersParam).catch((err) =>
                      console.error("Failed to restore filters:", err)
                    );
                  }
                }
              },
            },
            {
              locale: "en-US",
              sheetOptions: {
                initialSheetId: undefined,
                singleSheet: false,
                emitSizeChangedEventOnSheetChange: true,
              },
              toolbarOptions: {
                export: true,
                undoRedo: true,
                reset: true,
                bookmarks: true,
              },
              themeOptions: {
                themeArn: isDarkTheme ? THEMES.DARK : THEMES.LIGHT,
              },
              attributionOptions: {
                overlayContent: false,
              },
            }
          );
          dashboardExperienceRefs.current[activeKey] = experience;
        } catch (err) {
          console.error("Error embedding dashboard for stage:", activeKey, err);
          setError(
            err instanceof Error ? err : new Error("Failed to embed dashboard")
          );
          setIsLoading(false);
        }
      };

      embedDashboard();
    }
  }, [embedUrlData, stage, onLoad, isDarkTheme]);

  const formatQuickSightError = (err: unknown): string => {
    if (err instanceof Error) {
      try {
        const errorData = JSON.parse(
          err.message.replace("QuickSight Error: ", "")
        );
        return errorData.message?.[0] || err.message;
      } catch {
        return err.message.replace("QuickSight Error: ", "");
      }
    }
    return String(err);
  };

  if (error) {
    return (
      <Box padding="l">
        <Alert
          type="error"
          header="Error loading dashboard"
          dismissible={false}
        >
          {formatQuickSightError(error)}
        </Alert>
      </Box>
    );
  }

  const getAndSetUrlWithFilters = async () => {
    const filters = await getFilters();
    const newParams = new URLSearchParams(searchParams);
    newParams.set("filters", compressAndSerializeData(filters));
    setSearchParams(newParams);
    return window.location.href;
  };

  useEffect(() => {
    setShareHandler(isLoading ? null : () => getAndSetUrlWithFilters);
  }, [isLoading]);

  return (
    <Box padding="n">
      {isLoading && (
        <Box margin="xxl" textAlign="center">
          <Spinner size="large" />
        </Box>
      )}
      <div
        ref={mainContainerRef}
        style={{
          width: "100%",
          height: CONTAINER_HEIGHT,
          position: "relative",
          visibility: isLoading ? "hidden" : "visible",
          opacity: isLoading ? 0 : 1,
          transition: "opacity 0.3s ease-in-out",
        }}
      />
    </Box>
  );
};

export default QuickSightDashboard;
